<template>
    <div>
        <template v-if="isPdf">
            <canvas ref="canvas"></canvas><br>

            <div class="w-100 mt-2">
                <button @click.prevent="prev" class="btn btn-primary" :class="pageCount <= 1 ? 'd-none' : ''"><i class="fal fa-arrow-left"></i></button>
                <button @click.prevent="next" class="btn btn-primary pull-right"  :class="pageCount <= 1 ? 'd-none' : ''"><i class="fal fa-arrow-right"></i></button>
                <span>{{pageNum}} / {{pageCount}}</span>
            </div>
        </template>
        <template v-else>
            <img :src="pdflink" class="w-100">
        </template>

        <div class="w-100 mt-2">
            <br>
            <small>{{$t('pdfView.strange')}} <a :href="pdflink" target="_blank">{{$t('pdfView.openNewTab')}}</a></small>
        </div>
    </div>
</template>

<style scoped>
canvas, img {
    border: 1px solid lightgray;
    direction: ltr;
    width: 100%;
}
</style>

<script setup>
import { computed, onMounted, ref, shallowRef, watch } from "vue";

import loadPdfjs from "../pdfjs-loader";

const props = defineProps({
    pdflink: { type: String, default: undefined },
    isPdf: { type: Boolean, default: false },
});

const canvas = ref(null);
// shallowRef: a deep ref proxies pdf.js internals and breaks their private-field access
const pdfDoc = shallowRef(null);
const pageNum = ref(0);
const pageNumPending = ref(null);
const scale = 1.5;
const pageCount = computed(() => pdfDoc.value?.numPages ?? 0);

onMounted(async () => {
    if (props.isPdf) {
        await loadPdf();
    }
});

watch(() => props.pdflink, async () => {
    if (props.isPdf) {
        await loadPdf();
    }
});

watch(pdfDoc, async (document) => {
    if (document) {
        await renderPage(1);
        return;
    }

    const context = canvas.value.getContext("2d");
    context.clearRect(0, 0, canvas.value.width, canvas.value.height);
});

async function loadPdf() {
    if (!props.pdflink) {
        return;
    }

    const pdfjs = await loadPdfjs();
    pdfDoc.value = await pdfjs.getDocument({ url: props.pdflink }).promise;
}

async function renderPage(number) {
    pageNumPending.value = number;
    const page = await pdfDoc.value.getPage(number);
    const viewport = page.getViewport({ scale });
    canvas.value.width = viewport.width;
    canvas.value.height = viewport.height;
    await page.render({ canvasContext: canvas.value.getContext("2d"), viewport }).promise;
    pageNum.value = number;

    if (pageNumPending.value === number) {
        pageNumPending.value = null;
    } else {
        await renderPage(pageNumPending.value);
    }
}

async function prev() {
    if (pageNumPending.value !== null && pageNumPending.value > 1) {
        pageNumPending.value -= 1;
    } else if (pageNum.value > 1) {
        await renderPage(pageNum.value - 1);
    }
}

async function next() {
    if (pageNumPending.value !== null && pageNumPending.value < pdfDoc.value.numPages) {
        pageNumPending.value += 1;
    } else if (pageNum.value < pdfDoc.value.numPages) {
        await renderPage(pageNum.value + 1);
    }
}
</script>
